import { AsText, AsNumber, useModal, isAuthorised, refreshTokens  } from '@adserve/adserve-react-components';
import React, { useContext, useEffect, useMemo } from 'react';
import AsSelectWithPlaceholder from '../../generic/AsSelectWithPlaceholder';
import {
    cellPadding,
    commercialPanelStyle,
    compactTableInputStyle,
    labelStyle,
    inputStyle,
    statusInputStyle
} from './CampaignBriefStyles';
import { BusinessTypes, DealTypes } from '../../../constants/Enums';
import CompactTable from '../../generic/CompactTable/CompactTableNew';
import AccountNameCompactTableColumnDefinitions from './ColumnDefinitions/AccountNameCompactTableColumnDefinitions';
import { AccountsLookup } from './CampaignBriefLookupService';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import { SearchCampaignsByJCNStarts, ValidateJcn } from '../../../services/CampaignsDataService';
import PoNumberInput from './PoNumberInput';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';

const errorStyle = {
    borderColor: '#C40606',
    backgroundColor: '#FFF3F3'
};

const statusLabelStyle = {
    position: 'absolute',
    color: '#C40606',
    fontSize: 12
};

const CampaignCommercials = ({
    campaign,
    updateCampaign,
    billingTerms,
    vats,
    deals,
    setCurrentDeal,
    setDealManuallySelected,
    readOnly = false,
    validJcn,
    setValidJcn,
    setBillingAccountId,
    readOnlyProperty,
    bookedReadOnly = false,
    recalculateOffAirs,
    editAuthorisedOnly,
    campaignVersions,
    setReadOnly
}) => {
    const { formatNumber } = useContext(SystemSettingsContext);
    const { addMessage } = useModal();
    const { billingAccountChanged, setBillingAccountChanged } = useContext(AirtimePlanningContext);

    const isBooked = () => {
        return campaign?.campaignStatusName === 'Booked';
    }

    const isStarted = () => {
        return campaign?.campaignStatusName === 'Running' || new Date(Date.parse(campaign?.startDate)) <=  new Date();
    }

    const billingOptionsDisabled = () => {
        return (!isAuthorised('EDIT_BOOKED_CAM') && campaign?.campaignStatusName === 'Booked') || isStarted() || readOnly;
    }

    useEffect(() => {
        if (billingAccountChanged && isBooked) {
            addMessage({
                message: `${window.translate('Billing Account changed - please create and Book a new Quote to apply it to the Campaign')}`,
                buttons: [
                    {
                        text: window.translate('OK')
                    }
                ]
            });
        }
    }, [billingAccountChanged]);

    const bookedOrStarted = useMemo(() =>  billingOptionsDisabled(), [campaign?.campaignStatusName]);

    const validateJcn = async (searchValue) => {
        const result = await SearchCampaignsByJCNStarts(searchValue)
        let searchData = [];
        if (result?.length > 0) {
            if (campaign?.id === window.emptyGuid) {
                searchData = result.filter((p) => p.jcn !== '' && p.jcn === searchValue);
            } else {
                searchData = result.filter((p) => p.id !== campaign?.id && p.jcn !== '' && p.jcn === searchValue);
            }

            if (searchData?.length > 0) {
                updateCampaign('jcn', '');
                addMessage({
                    message: `${window.translate('This JCN has been used in an earlier campaign. Please use a different JCN number ')}`,
                    buttons: [
                        {
                            text: window.translate('Ok')
                        }
                    ]
                });
            }
        }

        return searchData.length === 0;
    };

    useEffect(() => {
        if (campaign?.jcn !== "1234567") {
            validateJcn(campaign?.jcn);
        }

        if (campaign?.jcn === "1234567") {
            updateCampaign('jcn', "");
            addMessage({
                message: `${window.translate('This is not a valid JCN number ')}`,
                buttons: [
                    {
                        text: window.translate('Ok')
                    }
                ]
            });
        }
    }, [campaign?.jcn])

    const handleChange = async (event) => {
        let { name, value } = event.target;
        if (name === 'barterCommissionRate' && value < 0) {
            value = 0;
        }
        if (name === 'barterCommissionRate' && value > 100) {
            value = 100;
        }
        updateCampaign(name, value);

        if (name === 'barterCommissionRate') {
            updateCampaign('barterCommissionRate', value);
            updateCampaign('commissionRate', 0);
        }
    };
    const setCampaignBudget = (e) => {
        const g = campaign.campaignGoal.find((s) => s.selected);
        g.value = e.target.value;
        if (g.goal === 'budget') {
            updateCampaign('budget', e.target.value);
        } else {
            updateCampaign('campaignGoal', campaign.campaignGoal);
        }
    };
    const handleBillingTermUpdate = (event) => {
        const currentBillingTerm = billingTerms.find((b) => b.id === event.target.value);
        campaign.billingTermName = currentBillingTerm?.name;
        updateCampaign('billingTermId', event.target.value);
        if (campaign.billingTermName === 'Bespoke') {
            updateCampaign('bespokeBillingNotSaved', true, false);
        } else {
            updateCampaign('bespokeBillingNotSaved', false, false);
        }
    };

    const handleAgencyDropdownClick = (searchResult) => {
        campaign.barterAgencyAccountId = searchResult.id;
        updateCampaign('barterAgencyAccountId', searchResult.id);
        updateCampaign('barterAgencyAccountName', searchResult.name);
        updateCampaign('barterCommissionRate', searchResult.commissionRate);
        updateCampaign('billingAccountId', searchResult.id);
        updateCampaign('billingAccountName', searchResult.name);
        updateCampaign('vatId', searchResult.vatId);
        setBillingAccountId(searchResult.id);
    };

    const handleAgencyChange = (event) => {
        updateCampaign('barterAgencyAccountName', event.target.value);
        updateCampaign('barterAgencyAccountId', null);
        if (event.target.value === '') {
            updateCampaign('billingAccountId', campaign.agencyAccountId);
            updateCampaign('billingAccountName', campaign.agencyAccountName);
            updateCampaign('commissionRate', campaign.commissionRate);
        }
    };

    const handleBillingAccountChange = (event) => {
        updateCampaign('billingAccountName', event.target.value);
    };

    const handleBillingAccountDropdownClick = (searchResult) => {
        campaign.billingAccountId = searchResult.id;
        updateCampaign('billingAccountName', searchResult.name);
        setBillingAccountId(searchResult.id);
        updateCampaign('commissionRate', searchResult.commissionRate);
        updateCampaign('vatId', searchResult.vatId);
        setBillingAccountChanged(true);
        setReadOnly(false);
    };

    const handlePoSingleChange = (event) => {
        if (campaign.poNumbersJson) {
            campaign.poNumbersJson.single = event.target.value;
            campaign.poNumbersJson.usingSingle = true;
        } else {
            campaign.poNumbersJson = {
                single: event.target.value,
                usingSingle: true
            };
        }
        updateCampaign('poNumbersJson', campaign.poNumbersJson);
    };

    const handlePoMonthlyChange = (value, idx) => {
        campaign.poNumbersJson.multiple[idx].poNumber = value;
        campaign.poNumbersJson.usingSingle = false;
        updateCampaign('poNumbersJson', campaign.poNumbersJson);
    };

    const setCommitType = (mode) => {
        campaign.campaignGoal.map((a, b) => (a.selected = false));
        campaign.campaignGoal.find((s) => s.goal === mode).selected = true;
        updateCampaign('campaignGoal', campaign.campaignGoal);
    };

    const isRunningCampaign = () => {
        return campaign.versionNumber > 0 && new Date(Date.parse(campaign?.startDate)) <= new Date();
    };

    const handleValidateJCN = async (event) => {
        const result = await ValidateJcn(event.target.value);
        setValidJcn(result);
    };

    const isRunningOrBooked = () => {
        return readOnly || bookedReadOnly || campaign?.campaignVersions?.length > 1 || isRunningCampaign();
    };

    const handleBarterAgencyRemoveClick = () => {
        updateCampaign('barterAgencyAccountName', '');
        updateCampaign('barterAgencyAccountId', null);

        if (campaign.agencyAccountId !== null) {
            updateCampaign('billingAccountId', campaign.agencyAccountId);
            updateCampaign('billingAccountName', campaign.agencyAccountName);
            updateCampaign('commissionRate', campaign.commissionRate);
            setBillingAccountId(campaign.agencyAccountId);
        } else {
            updateCampaign('billingAccountId', campaign.advertiserAccountId);
            updateCampaign('billingAccountName', campaign.advertiserAccountName);
            updateCampaign('commissionRate', 0);
            setBillingAccountId(campaign.advertiserAccountId);
        }
    };

    useEffect(() => {
        if (deals.length > 0 &&  campaignVersions?.length <= 1 && campaign?.campaignStatusName === 'Draft') {
            if (campaign?.agencyAccountId !== null) {
                const agency = deals.find((d) => d.type === DealTypes.AGENCY);
                if (agency) {
                    if (campaign?.dealVersionId !== agency.id) {
                        updateCampaign('dealVersionId', agency.id);
                        campaign.dealVersionName = agency.name;
                        campaign.campaignStations = [];
                        updateCampaign('dealVersionNumber', agency?.versionNumber);
                        setDealManuallySelected(true);
                    }

                    setCurrentDeal(agency);
                } else {
                    const currentDeal = deals.find((d) => d.type === DealTypes.BASE);
                    if (currentDeal) {
                        if (campaign?.dealVersionId !== currentDeal.id) {
                            updateCampaign('dealVersionId', currentDeal.id);
                            campaign.dealVersionName = currentDeal.name;
                            campaign.campaignStations = [];
                            updateCampaign('dealVersionNumber', currentDeal?.versionNumber);
                            setDealManuallySelected(true);
                        }

                        setCurrentDeal(currentDeal);
                    }
                }
            } else if (campaign?.advertiserAccountId !== null) {
                const advertiser = deals.find((d) => d.type === DealTypes.CLIENT);
                if (advertiser) {
                    if (campaign?.dealVersionId !== advertiser.id) {
                        updateCampaign('dealVersionId', advertiser.id);
                        campaign.dealVersionName = advertiser.name;
                        campaign.campaignStations = [];
                        updateCampaign('dealVersionNumber', advertiser?.versionNumber);
                        updateCampaign('dealVersionName', advertiser?.name);
                        setDealManuallySelected(true);
                    }

                    setCurrentDeal(advertiser);
                } else {
                    const currentDeal = deals.find((d) => d.type === DealTypes.BASE);
                    if (currentDeal) {
                        if (campaign?.dealVersionId !== currentDeal.id) {
                            updateCampaign('dealVersionId', currentDeal.id);
                            campaign.dealVersionName = currentDeal.name;
                            campaign.campaignStations = [];
                            updateCampaign('dealVersionNumber', currentDeal?.versionNumber);
                            setDealManuallySelected(true);
                        }

                        setCurrentDeal(currentDeal);
                    }
                }
            } else {
                const currentDeal = deals.find((d) => d.type === DealTypes.BASE);
                if (currentDeal) {
                    if (campaign?.dealVersionId !== currentDeal.id) {
                        updateCampaign('dealVersionId', currentDeal.id);
                        campaign.dealVersionName = currentDeal.name;
                        campaign.campaignStations = [];
                        updateCampaign('dealVersionNumber', currentDeal?.versionNumber);
                        setDealManuallySelected(true);
                    }

                    setCurrentDeal(currentDeal);
                }
            }
        } else {
            const currentDeal = deals.find((d) => d.id === campaign?.dealVersionId);
            setCurrentDeal(currentDeal);
        }
    }, [deals, campaign?.agencyAccountId, campaign?.advertiserAccountId]);

    return campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() ? (
        <div style={{ ...commercialPanelStyle, padding: '11px 10px 17px 188px' }}>
            <span style={{ fontSize: 15, fontWeight: 500, paddingLeft: 42 }}>
                {`${window.translate('Campaign Commercials')} *`}
            </span>
            <div style={{ display: 'flex', width: '100%' }}>
                <div style={{ width: '25%', padding: '8px 0px 0px 42px' }}>
                    <label style={labelStyle}>{window.translate('Barter Agency')}</label>
                    <CompactTable
                        id='campaign-barter-agency'
                        inputStyle={compactTableInputStyle}
                        value={campaign.barterAgencyAccountName}
                        onChange={handleAgencyChange}
                        searchFunction={(searchTerm, params) => AccountsLookup(searchTerm, params, 'barter')}
                        tableColumnDefinitions={AccountNameCompactTableColumnDefinitions}
                        onDropdownClick={handleAgencyDropdownClick}
                        disabled={readOnly || readOnlyProperty}
                        errorName='Barter Agency'
                        showClearButton={!!campaign.barterAgencyAccountName && !isRunningOrBooked()}
                        onRemoveClick={handleBarterAgencyRemoveClick}
                    />
                </div>
                <div style={{ width: '25%', padding: '8px 0px 0px 42px' }}>
                    <label style={labelStyle}>{window.translate('Billing Account')}</label>
                    <CompactTable
                        id='billing-account'
                        name='billingAccountId'
                        minSearchLength={0}
                        inputStyle={{
                            ...compactTableInputStyle,
                            width: '80%'
                        }}
                        value={campaign.billingAccountName}
                        onChange={handleBillingAccountChange}
                        searchFunction={(searchTerm, params) => AccountsLookup(searchTerm, params)}
                        tableColumnDefinitions={AccountNameCompactTableColumnDefinitions}
                        onDropdownClick={handleBillingAccountDropdownClick}
                        // disabled={readOnly || readOnlyProperty}
                        disabled={true}
                    />
                </div>
                <div style={{ display: 'flex', width: '40%', padding: '8px 0px 0px 42px' }}>
                    <div style={{ width: 140 }}>
                        <AsNumber
                            id='commission-rate'
                            name={!campaign.barterAgencyAccountId ? 'commissionRate' : 'barterCommissionRate'}
                            title={window.translate('Agency Commission %')}
                            labelStyle={labelStyle}
                            inputStyle={{ ...inputStyle, width: '76%' }}
                            value={
                                !campaign.barterAgencyAccountId
                                    ? campaign.commissionRate
                                    : campaign.barterCommissionRate
                            }
                            disabled={
                                readOnly || !campaign?.isAdminUser
                            }
                            allowFloat={true}
                            formatNumber={formatNumber}
                            digitLimit={5}
                            onChange={handleChange}
                        />
                    </div>
                    <div style={{ width: 140 }}>
                        <AsSelectWithPlaceholder
                            id='campaign-tax-rate-input'
                            name='vatId'
                            title={window.translate('Tax rate')}
                            values={vats}
                            value={campaign?.vatId}
                            onChange={handleChange}
                            disabled={readOnly || readOnlyProperty || !campaign?.isAdminUser}
                        />
                    </div>
                </div>
            </div>
        </div>
    ) : (
        <div style={{ ...commercialPanelStyle }}>
            <span style={{ fontSize: 15, fontWeight: 500, paddingLeft: 42 }}>
                {`${window.translate('Campaign Commercials')} *`}
            </span>
            <div style={{ display: 'flex', width: '100%' }}>
                <div style={{ width: '25%', padding: cellPadding }}>
                    <label style={labelStyle}>{window.translate('Barter Agency')}</label>
                    <CompactTable
                        id='campaign-barter-agency'
                        inputStyle={compactTableInputStyle}
                        value={campaign.barterAgencyAccountName}
                        onChange={handleAgencyChange}
                        searchFunction={(searchTerm, params) => AccountsLookup(searchTerm, params, 'barter')}
                        tableColumnDefinitions={AccountNameCompactTableColumnDefinitions}
                        onDropdownClick={handleAgencyDropdownClick}
                        disabled={readOnly}
                        errorName='Barter Agency'
                        showClearButton={!!campaign.barterAgencyAccountName && !isRunningOrBooked()}
                        onRemoveClick={handleBarterAgencyRemoveClick}
                    />
                </div>
                <div style={{ width: '25%', padding: cellPadding }}>
                    <label style={labelStyle}>{window.translate('Billing Account')}</label>
                    <CompactTable
                        id='billing-account'
                        name='billingAccountId'
                        minSearchLength={0}
                        inputStyle={{
                            ...compactTableInputStyle,
                            width: '80%'
                        }}
                        value={campaign.billingAccountName}
                        onChange={handleBillingAccountChange}
                        searchFunction={(searchTerm, params) => AccountsLookup(searchTerm, params)}
                        tableColumnDefinitions={AccountNameCompactTableColumnDefinitions}
                        onDropdownClick={handleBillingAccountDropdownClick}
                        disabled={bookedOrStarted}
                    />
                </div>
                <div style={{ width: '25%', padding: cellPadding }}>
                    <AsSelectWithPlaceholder
                        id='campaign-billing-terms-input'
                        name='billingTermId'
                        title={window.translate('Billing Terms')}
                        values={billingTerms}
                        value={campaign?.billingTermId}
                        onChange={handleBillingTermUpdate}
                        disabled={bookedOrStarted}
                    />
                </div>
                <div style={{ width: '25%', padding: cellPadding }}>
                    <PoNumberInput
                        readOnly={readOnly}
                        campaign={campaign}
                        handlePoMonthlyChange={handlePoMonthlyChange}
                        handlePoSingleChange={handlePoSingleChange}
                    />
                </div>
            </div>
            <div style={{ display: 'flex', width: '100%' }}>
                <div style={{ width: '25%', padding: cellPadding }}>
                    {(
                        // campaign?.businessType?.toLowerCase() === BusinessTypes.PACKAGE.name.toLowerCase() ||
                        campaign?.businessType?.toLowerCase() === BusinessTypes.AIRTIME.name.toLowerCase() ||
                        campaign?.businessType?.toLowerCase() === BusinessTypes.OFFAIRS.name.toLowerCase() ||
                        campaign?.businessType?.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase() ||
                        campaign?.businessType?.toLowerCase() === BusinessTypes.DIGITAL.name.toLowerCase()) && (
                            <AsSelectWithPlaceholder
                                id='campaign-deal-name'
                                name='dealVersionId'
                                inputStyle={{ width: '100%', fontSize: 15, fontWeight: 600 }}
                                title={window.translate('Deal name')}
                                placeholderText='No active deals are found'
                                values={deals}
                                value={campaign?.dealVersionId !== window.emptyGuid ? campaign?.dealVersionId : 0}
                                disabled={true}
                            />
                        )}
                </div>
                <div style={{ width: '25%', padding: cellPadding }}>
                    <div style={{ display: 'flex' }}>
                        <div>
                            <AsNumber
                                id='dealVersion-Number'
                                name='dealVersionNumber'
                                title={window.translate('Deal Version')}
                                labelStyle={labelStyle}
                                inputStyle={{ ...inputStyle, width: '90%' }}
                                value={
                                    campaign.dealVersionNumber ? campaign.dealVersionNumber : window.translate('None')
                                }
                                disabled={true}
                                allowFloat={true}
                                formatNumber={formatNumber}
                                digitLimit={5}
                            />
                        </div>
                        <div>
                            <AsNumber
                                id='commission-rate'
                                name={!campaign.barterAgencyAccountId ? 'commissionRate' : 'barterCommissionRate'}
                                title={window.translate('Agency Commission %')}
                                labelStyle={labelStyle}
                                inputStyle={{ ...inputStyle, width: '98%' }}
                                value={
                                    !campaign.barterAgencyAccountId
                                        ? campaign.commissionRate
                                        : campaign.barterCommissionRate
                                }
                                disabled={readOnly || !campaign?.isAdminUser}
                                allowFloat={true}
                                formatNumber={formatNumber}
                                digitLimit={5}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>
                <div style={{ width: '25%', padding: cellPadding }}>
                    <AsSelectWithPlaceholder
                        id='campaign-tax-rate-input'
                        name='vatId'
                        title={window.translate('Tax rate')}
                        values={vats}
                        value={campaign?.vatId}
                        onChange={handleChange}
                        disabled={readOnly || !campaign?.isAdminUser}
                    />
                </div>
                <div style={{ width: '25%', padding: cellPadding }}>
                    <AsText
                        id='JCN-number'
                        name='jcn'
                        title={window.translate('JCN number')}
                        labelStyle={labelStyle}
                        inputStyle={{
                            ...statusInputStyle,
                            width: '100%',
                            ...(!validJcn ? errorStyle : {})
                        }}
                        value={campaign.jcn}
                        disabled={readOnly}
                        onChange={handleChange}
                        onBlur={handleValidateJCN}
                    />
                    {!validJcn && (
                        <label style={statusLabelStyle}>{window.translate('Invalid JCN - check digit failed')}</label>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CampaignCommercials;
